import Cropper from 'cropperjs';
import * as rectLib from '../../../../../../common/maths/geometry/rect';
import { getImageEditorData } from '../../imageModalUtils';
import React from 'react';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { getIsTransparentImage } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type RenderableCropper = Cropper & {
    render: () => void;
};
export const positionImageInCanvasCenter = (cropper: Cropper) => {
    const { width, height } = cropper.getContainerData();
    const { naturalWidth, naturalHeight } = cropper.getImageData();

    const containerSize = Math.min(width, height);
    const imageSize = Math.min(naturalWidth, naturalHeight);
    const zoomScale = containerSize / imageSize;

    cropper.zoomTo(zoomScale, {
        x: width / 2,
        y: height / 2,
    });
};

/**
 * Initialises Cropper for the loaded editor image.
 *
 * `canUseLoadedImage` protects editor readiness from stale Cropper instances. Cropper's `ready`
 * callback can fire after React has already started loading a newer image.
 */
export const loadCanvasIntoCropper = (
    imageRef: React.RefObject<HTMLImageElement | null>,
    cropperRef: React.MutableRefObject<Cropper | null>,
    element: ImMNElement,
    setEditorIsReady: (ready: boolean) => void,
    isDirtyRef: React.MutableRefObject<boolean>,
    canUseLoadedImage: () => boolean = () => true,
    // When a background-removal toggle swaps the underlying source variant, the user's in-progress
    // rotate/flip/crop must survive the reload. The caller snapshots the live `cropper.getData()` here so
    // it can be re-applied to the swapped-in image instead of falling back to the stored `imageEditorData`.
    pendingCropperDataRef?: React.MutableRefObject<Cropper.Data | null>,
    onCropChange?: () => void,
) => {
    if (!imageRef.current) return;

    const imageEditorData = getImageEditorData(element) && asObject(getImageEditorData(element));

    cropperRef.current = new Cropper(imageRef.current, {
        dragMode: 'none',
        viewMode: 2,
        // Cropper's default container background is a white/grey checkerboard (`.cropper-bg`). The canvas is
        // fitted with fractional dimensions, so the checkerboard shows through the sub-pixel gap as a white
        // hairline at the image edges (on entering edit mode, and shifted around by flips/rotations). Only
        // transparent images want a checkerboard backdrop; everything else sits on the modal background.
        background: getIsTransparentImage(element),
        center: false,
        movable: false,
        zoomOnTouch: false,
        zoomOnWheel: false,
        toggleDragModeOnDblclick: false,
        autoCrop: true,
        autoCropArea: 1,
        checkOrientation: false,
        ready: () => {
            // Avoid letting a stale Cropper ready callback mark the current editor image as ready.
            if (!canUseLoadedImage()) return;

            const cropper = cropperRef.current;
            const pendingCropperData = pendingCropperDataRef?.current;

            if (cropper && pendingCropperData) {
                // Re-apply the in-progress rotate/flip/crop after a background-removal source swap so the
                // swapped-in variant keeps the orientation the user set, rather than snapping back to the
                // stored scale. `getData`/`setData` work in natural image coordinates, so the crop and flip
                // transfer cleanly between the same-sized with/without-background sources.
                //
                // Mirror the stored-data branch below: apply the transform+crop first, then re-apply the
                // crop selection after `crop()` so the crop box lands correctly, and only then re-center.
                const pendingSelectionData = {
                    x: pendingCropperData.x,
                    y: pendingCropperData.y,
                    width: pendingCropperData.width,
                    height: pendingCropperData.height,
                };
                cropper.clear();
                cropper.setData(pendingCropperData);
                cropper.crop();
                cropper.setData(pendingSelectionData);
                positionImageInCanvasCenter(cropper);
                if (pendingCropperDataRef) pendingCropperDataRef.current = null;
            } else if (cropper && imageEditorData) {
                const initialSelectionData = {
                    x: imageEditorData.x,
                    y: imageEditorData.y,
                    width: imageEditorData.width,
                    height: imageEditorData.height,
                };
                cropper.clear();
                cropper.setData(imageEditorData);
                cropper.crop();
                cropper.setData(initialSelectionData);
                positionImageInCanvasCenter(cropper);
            }

            setEditorIsReady(true);
        },
        cropmove: () => {
            isDirtyRef.current = true;
            onCropChange?.();
        },
    });
};

export const adjustCropperForRotation = (
    cropper: Cropper,
    modalWidth: number,
    initialCropData: Cropper.CropBoxData,
) => {
    const initialContainerData = cropper.getContainerData();

    // The modal might change scale when it gets rotated due to the a wide or tall aspect ratio.
    // We need to find out the scale change so that we can convert the initial crop rectangle into
    // the final crop rectangle
    const scalingFactor = modalWidth / rectLib.getHeight(initialContainerData);
    // Force Cropper to render with the new rotation before updating the crop box.
    // `render()` exists at runtime even though it is not exposed on the public type.
    (cropper as RenderableCropper).render();

    let newCropRect = {
        top: initialContainerData.width - initialCropData.left - initialCropData.width,
        left: initialCropData.top,
        width: initialCropData.height,
        height: initialCropData.width,
    };
    newCropRect = rectLib.asRect(newCropRect);
    // @ts-ignore - rectlib.scale uses lodash curry, which breaks the type inference
    newCropRect = rectLib.scale(scalingFactor, newCropRect);

    cropper.setCropBoxData({
        top: rectLib.getTop(newCropRect),
        left: rectLib.getLeft(newCropRect),
        width: rectLib.getWidth(newCropRect),
        height: rectLib.getHeight(newCropRect),
    });
};
